import { CommonModule } from '@angular/common';
import { inject, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import {
    AiChatWidgetComponent,
    createActionsSuggestionsResponseInterceptor,
    createKnowledgeBaseThinkingInterceptor,
    createScriptBuilderThinkingInterceptor,
    createSuggestionsInterceptor,
    createThinkingResponseInterceptor,
    createUrlBuilderThinkingInterceptor,
    provideAiChatEventNotifier
} from '@panaya/ai-chat/features';
import { seemoreCanvasProviders } from './seemore/seemore-canvas.providers';
import { CommonGridModule } from '@frontend-site/grid/common-grid';
import { MainHeaderDomainModule } from '@frontend-site/main-header/domain';
import { EmptyStateComponent, PChipModule, PDialogModule, PFieldModule, PSnackbarModule } from '@frontend-site/ui/components';
import { UsersFeaturesModule } from '@frontend-site/users/features';
import { TranslateModule } from '@ngx-translate/core';
import { PIconModule } from '@panaya/ui/p-icon';
import { PLinkModule } from '@panaya/ui/p-link';
import { PTooltipModule } from '@panaya/ui/p-tooltip';
import { PipeModule } from '@panaya/ui/pipes';
import { NgLetModule } from 'ng-let';
import { MainHeaderActionsComponent } from './main-header-actions/main-header-actions.component';
import { MainHeaderComponent } from './main-header.component';
import { MegaMenuColumnComponent } from './megaMenu-column/megaMenu-column.component';
import { MegaMenuContainerComponent } from './megaMenu-container/megaMenu-container.component';
import { NavItemComponent } from './nav-item/nav-item.component';
import { PanayaLogoComponent } from './panaya-logo/panaya-logo.component';
import { RequestStatusDialogComponent } from './request-status/dialogs/request-status-dialog/request-status-dialog.component';
import { RequestStatusCellRendererComponent } from './request-status/dialogs/request-status-dialog/request-status-renderer/request-status-cell-renderer/request-status-cell-renderer.component';
import { RecentlyViewedItemComponent } from './search/recently-viewed-item/recently-viewed-item.component';
import { SearchComponent } from './search/search.component';
import { SettingsComponent } from './settings/settings.component';
import { UserItemComponent } from './user-notifications/user-item/user-item.component';
import { UserNotificationsComponent } from './user-notifications/user-notifications.component';
import { AiChatUiEventsService, promptBarFileInstructionInterceptor, SUGGESTION_ICON_MAP } from '@panaya/ai-chat/ui';
import { provideWidgetConfig } from '@panaya/ai-chat/utils';
import { UserService } from '@frontend-site/users/domain';
import { SeemoreUiViewContextService } from '@frontend-site/shared/core';
import { PButtonModule } from '@panaya/ui/p-button';
import {
    provideCustomAiChatRequestService,
    provideInitThreadResponseInterceptor,
    providePromptResponseInterceptor,
    provideStreamingPromptResponseInterceptor,
    provideSuggestionsResponseInterceptor
} from '@panaya/ai-chat/domain';
import { AiChatService } from './seemore';

@NgModule({
    imports: [
        CommonModule,
        RouterModule,
        PIconModule,
        PButtonModule,
        PDialogModule,
        PFieldModule,
        PLinkModule,
        PSnackbarModule,
        TranslateModule,
        MainHeaderDomainModule,
        MatMenuModule,
        MatButtonModule,
        MatButtonModule,
        MatTooltipModule,
        FormsModule,
        ReactiveFormsModule,
        MatExpansionModule,
        PTooltipModule,
        NgLetModule,
        PipeModule,
        EmptyStateComponent,
        MatListModule,
        CommonGridModule,
        UsersFeaturesModule,
        PChipModule,
        AiChatWidgetComponent
        // .config({ chatLoaderUrl: 'http://localhost:3000/ai-chatbot-widget' })
    ],
    declarations: [
        MainHeaderComponent,
        NavItemComponent,
        MegaMenuContainerComponent,
        MegaMenuColumnComponent,
        SettingsComponent,
        MainHeaderActionsComponent,
        PanayaLogoComponent,
        RecentlyViewedItemComponent,
        UserNotificationsComponent,
        UserItemComponent,
        SearchComponent,
        UserItemComponent,
        RequestStatusDialogComponent,
        RequestStatusCellRendererComponent
    ],
    providers: [
        provideWidgetConfig({
            emptyStateImageURL: 'assets/icons/svgs/seemore-working.svg',
            triggerImageURL: () =>
                inject(UserService).isExecuteIaUser()
                    ? 'assets/icons/svgs/copilot-launcher-dark.svg'
                    : 'assets/icons/svgs/copilot-launcher.svg',
            triggerImageHoveredURL: 'assets/icons/svgs/copilot-launcher-hovered.svg',
            triggerImageCustomClass: 'trigger-img',
            aiApiUrl: '/rest/llm/chat',
            appUrl: '/',
            dataSourceUrl: 'https://success.panaya.com/docs/',
            context: {
                applicationArea: 'TDX',
                urlGetter: () => location.href,
                uiViewContextGetter: () => inject(SeemoreUiViewContextService).getActiveViewContext()
            },
            aiChatWindowCustomClass: 'ai-chat-window'
        }),
        {
            provide: SUGGESTION_ICON_MAP,
            useValue: {
                Search: 'search',
                'Success Center': 'message-square'
            }
        },
        provideStreamingPromptResponseInterceptor(createThinkingResponseInterceptor),
        provideStreamingPromptResponseInterceptor(createKnowledgeBaseThinkingInterceptor),
        provideStreamingPromptResponseInterceptor(createScriptBuilderThinkingInterceptor),
        provideStreamingPromptResponseInterceptor(createUrlBuilderThinkingInterceptor),
        ...seemoreCanvasProviders,
        providePromptResponseInterceptor(promptBarFileInstructionInterceptor),
        providePromptResponseInterceptor(createActionsSuggestionsResponseInterceptor),
        provideInitThreadResponseInterceptor(createSuggestionsInterceptor),
        provideSuggestionsResponseInterceptor(createSuggestionsInterceptor),
        provideAiChatEventNotifier(AiChatUiEventsService),
        provideCustomAiChatRequestService(AiChatService)
    ],
    exports: [MainHeaderComponent]
})
export class MainHeaderModule {}
